import { mixOklab } from "./colorMix";

/**
 * A class cover: a triplet {a deep, b mid, c light} plus a shape (0-3) picking a texture.
 * Everything representing a class in the UI derives from this — never a hardcoded colour.
 */
export interface Cover {
  a: string;
  b: string;
  c: string;
  shape: number;
}

export interface CoverConfig {
  hex?: string;
  color?: number;
  shape: number;
}

export type CoverTriplet = Omit<Cover, "shape">;

/** Default palette — 8 covers, assigned in class creation order. */
export const COVERS: readonly CoverTriplet[] = [
  { a: "#26324a", b: "#7b93bd", c: "#b9c8dd" },
  { a: "#6d2f45", b: "#e07b99", c: "#f6c8d6" },
  { a: "#6b4526", b: "#d8ab6c", c: "#f0d9b4" },
  { a: "#3d2f46", b: "#9d84a8", c: "#d8cadd" },
  { a: "#414a37", b: "#a3a97a", c: "#dcdcbe" },
  { a: "#5f302f", b: "#c98f82", c: "#e9c8bd" },
  { a: "#243f4a", b: "#7cabb7", c: "#c6dee2" },
  { a: "#413a2c", b: "#b2a184", c: "#e0d5c2" },
] as const;

/** Gradient angle per shape. */
export const COVER_ANGLES: readonly number[] = [152, 196, 128, 168] as const;

export const SHAPE_COUNT = 4;

/**
 * Derives a triplet from a user-picked colour.
 * The handoff expresses this with color-mix(in oklab, …); it is resolved here instead.
 */
export function coverFromHex(hex: string): CoverTriplet {
  return {
    a: mixOklab(hex, 0.46, "#14161c"),
    b: hex,
    c: mixOklab(hex, 0.44, "#ffffff"),
  };
}

/**
 * Resolves the cover of a class.
 * @param name    class name
 * @param order   ordered list of all classes — the index picks the default cover
 * @param configs per-class customisations
 */
export function coverOf(
  name: string,
  order: readonly string[],
  configs: Readonly<Record<string, CoverConfig>> = {},
): Cover {
  const config = configs[name];

  if (config?.hex) {
    return { ...coverFromHex(config.hex), shape: config.shape % SHAPE_COUNT };
  }

  if (config) {
    return {
      ...COVERS[(config.color ?? 0) % COVERS.length],
      shape: config.shape % SHAPE_COUNT,
    };
  }

  const index = Math.max(0, order.indexOf(name));
  return { ...COVERS[index % COVERS.length], shape: index % SHAPE_COUNT };
}